import { t } from '@trimble/i18n';
import type { LengthUnit } from '@fulcrum/lib';
import {
  Component,
  createEffect,
  createMemo,
  For,
  onCleanup,
  Show,
  type JSX,
} from 'solid-js';
import type { EditablePalette, EditableStop } from '../../types/heatmap.types';
import {
  convertStopValueToDisplay,
  EDITABLE_STOP_LAYOUT,
  barPickOffsetYToValueInMeters,
  largestGapMidpointValueInMeters,
  formatOverflowLabel,
  formatStopValueForDisplay,
  overflowAboveRowTopPx,
  overflowBelowRowTopPx,
  overflowStopColors,
  stopListHeightPx,
  stopRowTopPxMap,
  stopValueRange,
  STOP_ROW_HEIGHT_PX,
} from '../../heatmap-stop-layout';
import { GradientStopBar } from './GradientStopBar';
import { OverflowStopRow } from './OverflowStopRow';
import { StopValueRow } from './StopValueRow';
import { HeatmapSectionLabel } from '../../components/HeatmapSectionLabel';
import {
  flipAnimatePaletteStopRows,
  snapshotPaletteStopRowPositions,
} from './palette-stop-row-flip';

/** The measurements API requires a palette to keep at least two stops. */
const MIN_STOP_COUNT = 2;

export interface PaletteStopViewerProps {
  readonly palette: EditablePalette;
  readonly lengthUnit?: LengthUnit;
  readonly editable: boolean;
  readonly duplicateStopIds?: ReadonlySet<string>;
  /** Commit a stop value edit (blur / Enter); parent updates and re-sorts in one write. */
  onStopValueCommit?(id: string, value: string): void;
  /** Reports whether an editable stop row's draft parses as a valid number. */
  onStopDraftValidityChange?(stopId: string, valid: boolean): void;
  onRemoveStop?(id: string): void;
  onStopColorChange?(id: string, color: string): void;
  /** Change the out-of-range-above cap colour. */
  onAboveColorChange?(color: string): void;
  /** Change the out-of-range-below cap colour (gradient only). */
  onBelowColorChange?(color: string): void;
  /** Insert a stop at the picked bar position (value in metres). */
  onBarInsertStop?(valueInMeters: number): void;
  /** Stop id to highlight on the bar and rows during edit/drag. */
  readonly activeStopId?: string | null;
  /** Stop id whose value input should be focused (bar insert only). */
  readonly focusStopId?: string | null;
  /** Live display value for the stop currently being dragged on the bar. */
  draggingStopDisplayValueById?(stopId: string): string | undefined;
  /** Clears {@link activeStopId} when the user focuses another stop row. */
  onClearActiveStop?(): void;
  onStopDragDraft?(stopId: string, valueInMeters: number): void;
  onStopDragCommit?(stopId: string, valueInMeters: number): void;
  onStopActivate?(stopId: string): void;
}

/**
 * Palette stop column: vertical bar, overflow rows, and value rows. Pass
 * `editable={false}` for read-only preview; `editable={true}` plus callbacks
 * turns value rows into inputs (with remove controls) and animates re-sorting on
 * commit. The section label is omitted in editable mode because
 * `EditPalettePanel` supplies it.
 */
export const PaletteStopViewer: Component<PaletteStopViewerProps> = (props) => {
  let stopRowsContainer: HTMLDivElement | undefined;

  const lengthUnit = (): LengthUnit => props.lengthUnit ?? 'm';

  const stops = (): readonly EditableStop[] => props.palette.stops;

  const canRemoveStop = (): boolean => stops().length > MIN_STOP_COUNT;

  const duplicateStopIds = (): ReadonlySet<string> =>
    props.duplicateStopIds ?? new Set<string>();

  const isDuplicateStop = (stopId: string): boolean =>
    duplicateStopIds().has(stopId);

  const valueRange = createMemo(() => stopValueRange(stops()));

  const overflowColors = createMemo(() =>
    overflowStopColors(
      stops(),
      {
        above: props.palette.colorAboveRange,
        below: props.palette.colorBelowRange,
      },
      props.palette.textureType
    )
  );

  const overflowLabels = createMemo(() => {
    const unit = lengthUnit();
    return {
      above: formatOverflowLabel(
        'above',
        convertStopValueToDisplay(valueRange().max, unit),
        unit
      ),
      below: formatOverflowLabel(
        'below',
        convertStopValueToDisplay(valueRange().min, unit),
        unit
      ),
    };
  });

  const listHeightPx = (): number => stopListHeightPx(stops().length);

  const stopRowTopPxById = createMemo(() =>
    stopRowTopPxMap(stops(), props.palette.textureType, {
      // Both modes clamp clustered rows to the row height so labels, inputs,
      // and remove controls never overlap.
      ...EDITABLE_STOP_LAYOUT,
      textureType: props.palette.textureType,
    })
  );

  const stopRowLayoutKey = createMemo(() =>
    stops()
      .map(
        (stop) => `${stop.id}:${String(stopRowTopPxById().get(stop.id) ?? 0)}`
      )
      .join('|')
  );

  // FLIP: animate rows from their previous layout to the new one when stop
  // order or target row tops change (add, remove, or value-edit re-sort).
  // Only the editable editor animates; unrelated form edits must not move rows.
  let previousRowTops: ReadonlyMap<string, number> | undefined;
  let previousLayoutKey: string | undefined;
  let previousTextureType: EditablePalette['textureType'] | undefined;

  createEffect(() => {
    if (!props.editable) return;

    const textureType = props.palette.textureType;
    const layoutKey = stopRowLayoutKey();

    if (!stopRowsContainer) return;

    const textureChanged =
      previousTextureType !== undefined && previousTextureType !== textureType;
    previousTextureType = textureType;

    if (textureChanged) {
      previousLayoutKey = layoutKey;
      previousRowTops = snapshotPaletteStopRowPositions(stopRowsContainer);
      return;
    }

    if (previousLayoutKey === undefined) {
      previousLayoutKey = layoutKey;
      previousRowTops = snapshotPaletteStopRowPositions(stopRowsContainer);
      return;
    }

    if (layoutKey === previousLayoutKey) {
      return;
    }

    previousLayoutKey = layoutKey;

    const first = previousRowTops;
    if (first && first.size > 0) {
      onCleanup(
        flipAnimatePaletteStopRows(stopRowsContainer, first, (positions) => {
          if (stopRowsContainer) {
            previousRowTops =
              positions ?? snapshotPaletteStopRowPositions(stopRowsContainer);
          }
        })
      );
      return;
    }

    previousRowTops = snapshotPaletteStopRowPositions(stopRowsContainer);
  });

  const overflowBelowTopPx = (): number =>
    overflowBelowRowTopPx(stops().length);

  const overflowRowStyle = (topPx: number): JSX.CSSProperties => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: `${String(topPx)}px`,
    height: `${String(STOP_ROW_HEIGHT_PX)}px`,
    display: 'flex',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'align-items': 'center',
  });

  function handleBarClick(offsetY: number): void {
    if (!props.editable || props.onBarInsertStop === undefined) return;
    const valueInMeters = barPickOffsetYToValueInMeters(
      offsetY,
      stops(),
      props.palette.textureType
    );
    props.onBarInsertStop(valueInMeters);
  }

  function handleBarKeyboardInsert(): void {
    if (!props.editable || props.onBarInsertStop === undefined) return;
    props.onBarInsertStop(largestGapMidpointValueInMeters(stops()));
  }

  return (
    <div
      class={`palette-stop-viewer flex flex-col gap-1${
        props.editable ? ' pt-4' : ''
      }`}
      data-testid="palette-stop-viewer"
    >
      <Show when={!props.editable}>
        <HeatmapSectionLabel
          uppercase
          label={t('measurements.volumetric.gradient.colors')}
        />
      </Show>

      <div
        class="relative flex items-start gap-2"
        data-testid="palette-stop-rows"
      >
        <GradientStopBar
          stops={stops()}
          textureType={props.palette.textureType}
          colorAboveRange={props.palette.colorAboveRange}
          colorBelowRange={props.palette.colorBelowRange}
          editable={props.editable}
          selectedStopId={props.activeStopId ?? null}
          draggingStopId={props.activeStopId ?? null}
          onBarClick={
            props.editable && props.onBarInsertStop !== undefined
              ? handleBarClick
              : undefined
          }
          onBarKeyboardInsert={
            props.editable && props.onBarInsertStop !== undefined
              ? handleBarKeyboardInsert
              : undefined
          }
          onStopDragDraft={props.editable ? props.onStopDragDraft : undefined}
          onStopDragCommit={props.editable ? props.onStopDragCommit : undefined}
          onStopActivate={props.editable ? props.onStopActivate : undefined}
        />

        <div
          class="relative flex-1 min-w-0"
          style={{
            position: 'relative',
            height: `${String(listHeightPx())}px`,
          }}
          ref={(el) => {
            stopRowsContainer = el;
          }}
        >
          <div style={overflowRowStyle(overflowAboveRowTopPx())}>
            <OverflowStopRow
              color={overflowColors().above}
              label={overflowLabels().above}
              testId="palette-overflow-above"
              editable={props.editable}
              onColorChange={props.onAboveColorChange}
            />
          </div>

          <For each={stops()}>
            {(stop) => (
              <div
                class="absolute left-0 right-0 flex items-center"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${String(stopRowTopPxById().get(stop.id) ?? 0)}px`,
                  height: `${String(STOP_ROW_HEIGHT_PX)}px`,
                }}
                data-stop-id={stop.id}
              >
                <StopValueRow
                  color={stop.color}
                  initialValue={formatStopValueForDisplay(
                    stop.value,
                    lengthUnit()
                  )}
                  lengthUnit={lengthUnit()}
                  testId={`palette-stop-${stop.id}`}
                  editable={props.editable}
                  selected={props.activeStopId === stop.id}
                  focusValueInput={props.focusStopId === stop.id}
                  valueDisplayOverride={props.draggingStopDisplayValueById?.(
                    stop.id
                  )}
                  onValueInputFocus={() => {
                    if (
                      props.activeStopId !== undefined &&
                      props.activeStopId !== null &&
                      props.activeStopId !== stop.id
                    ) {
                      props.onClearActiveStop?.();
                    }
                  }}
                  removeDisabled={!canRemoveStop()}
                  hasError={isDuplicateStop(stop.id)}
                  onValueCommit={(value) =>
                    props.onStopValueCommit?.(stop.id, value)
                  }
                  onDraftValidityChange={(valid) =>
                    props.onStopDraftValidityChange?.(stop.id, valid)
                  }
                  onRemove={() => props.onRemoveStop?.(stop.id)}
                  onColorChange={(color) =>
                    props.onStopColorChange?.(stop.id, color)
                  }
                />
              </div>
            )}
          </For>

          <Show when={props.palette.textureType !== 'stepped'}>
            <div style={overflowRowStyle(overflowBelowTopPx())}>
              <OverflowStopRow
                color={overflowColors().below}
                label={overflowLabels().below}
                testId="palette-overflow-below"
                editable={props.editable}
                onColorChange={props.onBelowColorChange}
              />
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
